import { type Data } from 'core/domain/Request'
import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import sentry from 'legacy/utils/sentry'

import { type PostFacilityReviewPayload } from '../domain/FacilityReviews'
import {
  mapApiToFacility,
  mapApiToFacilityContact,
  mapApiToFacilityHours,
  mapApiToFacilityPerformance,
  mapApiToFacilityReport,
  type RawFacility,
  type RawFacilityContact,
  type RawFacilityHours,
  type RawFacilityPerformance,
  type RawFacilityReport,
} from './facilityMapper'

type FacilityRepositoryDeps = {
  httpClient: HttpClientInterface
}

export class FacilityRepository {
  httpClient: HttpClientInterface

  constructor({ httpClient }: FacilityRepositoryDeps) {
    this.httpClient = httpClient
  }

  async getFacilityById(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/api/v2/shippers/facilities/${uuid}`
    const { data } = await this.httpClient.get<Data<RawFacility>>(endpoint, config)

    try {
      return mapApiToFacility(data.data)
    } catch (e) {
      sentry.log(e)
      throw new Error('Failed to parse Facility', { cause: e })
    }
  }

  async getFacilityPerformance(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/api/v2/facilities/${uuid}`
    const { data } = await this.httpClient.get<Data<RawFacilityPerformance>>(endpoint, config)

    try {
      return mapApiToFacilityPerformance(data.data)
    } catch (e) {
      sentry.log(e)
      throw new Error('Failed to parse Facility Performance', { cause: e })
    }
  }

  async getFacilityHours(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/api/v2/shippers/facilities/${uuid}/working-hours`
    const { data } = await this.httpClient.get<Data<[RawFacilityHours]>>(endpoint, config)
    const [rawHours] = data.data ?? []

    try {
      return mapApiToFacilityHours(rawHours)
    } catch (e) {
      sentry.log(e)
      throw new Error('Failed to parse Facility Hours', { cause: e })
    }
  }

  async getFacilityContacts(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/api/v2/shippers/facilities/${uuid}/contacts`
    const { data } = await this.httpClient.get<Data<[RawFacilityContact]>>(endpoint, config)

    try {
      return data.data.map(rawContact => mapApiToFacilityContact(rawContact))
    } catch (e) {
      sentry.log(e)
      throw new Error('Failed to parse Facility Contacts', { cause: e })
    }
  }

  async getFacilityReportById(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/api/v2/facilities/${uuid}/review-report`
    const { data } = await this.httpClient.get<Data<RawFacilityReport>>(endpoint, config)

    try {
      return mapApiToFacilityReport(data.data)
    } catch (e) {
      throw new Error('Failed to parse Facility Report', { cause: e })
    }
  }

  async postFacilityReview(
    { uuid, score, tags, reviewUuid }: PostFacilityReviewPayload,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/api/v2/facilities/${uuid}/reviews`
    const payload = {
      facility_review_request_uuid: reviewUuid,
      score,
      tags,
    }
    return this.httpClient.post<Data<void>>(endpoint, payload, config)
  }
}
